/**
 * One integration's management page (task 69; Stories 3, 10, 18): the
 * connector's declared capabilities, then every visible Client's
 * connection to it — state, health, last sync, watermark, 24-hour volume,
 * last error — and, where the caller holds `manage_client` on that Client
 * (Story 22), the credential controls.
 *
 * Which controls exist is decided by how the integration connects
 * (`lib/integrations.ts`): a token integration gets Add / Rotate / Remove;
 * a consent integration gets Re-mint invite while the Client is pending.
 * Story 19 adds the response credentials beside them: a token integration
 * with a `responseCredential` gets Add / Rotate / Remove for its
 * write-scoped token, and a consent integration gets Update permissions
 * when a Client's consent predates what the app now declares.
 *
 * Task 161 adds `mapping` integrations (NinjaOne, ConnectSecure): a platform
 * credential card above the table, and per Client a Map / Change / Unmap to
 * one of the vendor's organizations. Every mapping move is `manage_platform`.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import {
  getPlatformIntegration,
  listIntegrations,
  listTenants,
  putEntraLogsSource,
  putSentinelOneResponseToken,
  putSentinelOneSource,
  putSourceMapping,
  reconsentTenant,
  reissueInvite,
  removeSentinelOneResponseToken,
  removeSource,
  type ClientTenant,
  type CreateTenantResult,
  type EventFeed,
  type IntegrationName,
  type PlatformIntegrationName,
  type ReconsentResult,
  type SourceInfo,
} from '@bluesphere/api-client';

import { useAccessToken } from '../auth/useAccessToken';
import { useTenant } from '../auth/useTenant';
import { ConfirmModal } from '../components/ConfirmModal';
import { GenericSources } from '../components/GenericSources';
import { InviteModal } from '../components/InviteModal';
import {
  absoluteTime,
  ConnectErrorNote,
  ErrorNote,
  feedLabel,
  sourceLabel,
  timeAgo,
} from '@bluesphere/mdr-alert-panel';
import { ReconsentModal } from '../components/ReconsentModal';
import { ResponseTokenModal } from '../components/ResponseTokenModal';
import { SentinelOneModal } from '../components/SentinelOneModal';
import { PageNav, backLinkClass } from '../components/PageNav';
import {
  PlatformCredentialCard,
  VendorMappingModal,
  mappedName,
  vendorNoun,
} from '../components/PlatformIntegration';
import { CapabilitiesLine, syncSummary } from '../components/SourceHealth';
import { ConnectionBadge } from '../components/StatusBadge';
import { EmptyState, ErrorState, HealthChip, Loading } from '../components/states';
import { isSignInFeed } from '../lib/eventSearch';
import {
  ENTRA_LOGS_SCOPE,
  INTEGRATION_PAGES,
  connectionState,
  isIntegrationName,
  type PagedIntegrationName,
} from '../lib/integrations';

export function IntegrationDetail({ name }: { name: string }) {
  if (!isIntegrationName(name)) {
    return (
      <div className="bs-card p-16 text-center">
        <p className="text-sm font-semibold text-bluesphere-textOnLight">Unknown integration</p>
        <p className="mt-1 text-sm text-bluesphere-muted">
          No connector named <span className="font-mono">{name}</span>.
        </p>
        <Link to="/integrations" className={`${backLinkClass} mt-4`}>
          ← Back to Integrations
        </Link>
      </div>
    );
  }
  // Task 211: generic sources are many per Client, each with its own key.
  if (name === 'generic') return <GenericSources />;
  return <KnownIntegration name={name} />;
}

function KnownIntegration({ name }: { name: PagedIntegrationName }) {
  const idToken = useAccessToken();
  const { can } = useTenant();
  const queryClient = useQueryClient();
  const page = INTEGRATION_PAGES[name];

  const integrations = useQuery({
    queryKey: ['integrations'],
    queryFn: () => listIntegrations(idToken),
    enabled: Boolean(idToken),
    staleTime: 5 * 60_000,
  });
  const tenants = useQuery({
    queryKey: ['tenants'],
    queryFn: async () => (await listTenants(idToken)).tenants,
    enabled: Boolean(idToken),
    refetchInterval: 15_000,
  });
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['tenants'] });
  // Task 161: a mapping integration's platform credential and its mapping moves.
  const mapping = page.connectsBy === 'mapping';
  const vendor = name as PlatformIntegrationName;
  const platform = useQuery({
    queryKey: ['platform-integration', name],
    queryFn: () => getPlatformIntegration(vendor, idToken),
    enabled: Boolean(idToken) && mapping,
  });
  const [mapFor, setMapFor] = useState<ClientTenant | null>(null);
  const mapMutation = useMutation<
    ClientTenant,
    Error,
    { clientTenantId: string; externalId: string; addService?: boolean }
  >({
    mutationFn: ({ clientTenantId, externalId, addService }) =>
      putSourceMapping(clientTenantId, vendor, externalId, idToken, { addService }),
    onSuccess: () => {
      setMapFor(null);
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['platform-integration', name] });
      void queryClient.invalidateQueries({ queryKey: ['vendor-accounts', name] });
    },
  });

  const [credentialFor, setCredentialFor] = useState<ClientTenant | null>(null);
  const [pendingRemove, setPendingRemove] = useState<ClientTenant | null>(null);
  const [activeInvite, setActiveInvite] = useState<CreateTenantResult | null>(null);
  const [responseTokenFor, setResponseTokenFor] = useState<ClientTenant | null>(null);
  const [pendingTokenRemove, setPendingTokenRemove] = useState<ClientTenant | null>(null);
  const [reconsent, setReconsent] = useState<{ tenant: ClientTenant; result: ReconsentResult } | null>(
    null,
  );

  const credentialMutation = useMutation<
    ClientTenant,
    Error,
    { clientTenantId: string; consoleUrl: string; apiToken: string; addService?: boolean }
  >({
    mutationFn: ({ clientTenantId, consoleUrl, apiToken, addService }) =>
      putSentinelOneSource(
        clientTenantId,
        { console_url: consoleUrl, api_token: apiToken },
        idToken,
        { addService },
      ),
    onSuccess: () => {
      setCredentialFor(null);
      invalidate();
    },
  });
  const removeMutation = useMutation<ClientTenant, Error, { clientTenantId: string }>({
    mutationFn: ({ clientTenantId }) => removeSource(clientTenantId, name, idToken),
    onSuccess: () => {
      setPendingRemove(null);
      invalidate();
      if (mapping) {
        void queryClient.invalidateQueries({ queryKey: ['platform-integration', name] });
        void queryClient.invalidateQueries({ queryKey: ['vendor-accounts', name] });
      }
    },
  });
  const reissueMutation = useMutation<CreateTenantResult, Error, { clientTenantId: string }>({
    mutationFn: ({ clientTenantId }) => reissueInvite(clientTenantId, idToken),
    onSuccess: (data) => {
      setActiveInvite(data);
      invalidate();
    },
  });

  const responseTokenMutation = useMutation<
    ClientTenant,
    Error,
    { clientTenantId: string; apiToken: string }
  >({
    mutationFn: ({ clientTenantId, apiToken }) =>
      putSentinelOneResponseToken(clientTenantId, { api_token: apiToken }, idToken),
    onSuccess: () => {
      setResponseTokenFor(null);
      invalidate();
    },
  });
  const removeTokenMutation = useMutation<ClientTenant, Error, { clientTenantId: string }>({
    mutationFn: ({ clientTenantId }) => removeSentinelOneResponseToken(clientTenantId, idToken),
    onSuccess: () => {
      setPendingTokenRemove(null);
      invalidate();
    },
  });
  // Task 157: an `enable` integration is switched on per Client. A 409
  // consent_required comes back as the error shown above the table.
  const enableMutation = useMutation<
    ClientTenant,
    Error,
    { clientTenantId: string; addService?: boolean }
  >({
    mutationFn: ({ clientTenantId, addService }) =>
      putEntraLogsSource(clientTenantId, idToken, { addService }),
    onSuccess: () => invalidate(),
  });
  const reconsentMutation = useMutation<ReconsentResult, Error, { tenant: ClientTenant }>({
    mutationFn: ({ tenant }) => reconsentTenant(tenant.client_tenant_id, idToken),
    onSuccess: (result, { tenant }) => setReconsent({ tenant, result }),
  });

  const manifest = integrations.data?.integrations.find((i) => i.name === name)?.manifest;
  const rows = (tenants.data ?? [])
    .filter((t) => t.status !== 'revoked')
    .sort((a, b) => a.display_name.localeCompare(b.display_name));
  const connected = rows.filter((t) => connectionState(t, name) === 'connected').length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <PageNav backTo="/integrations" backLabel="Integrations" />
          <h1 className="mt-3 text-2xl font-semibold">{sourceLabel(name)}</h1>
          <p className="mt-1 max-w-3xl text-sm text-white/60">{page.description}</p>
        </div>
        <div className="rounded-full bg-white/10 px-4 py-2 text-sm text-white">
          {connected} of {rows.length} Client{rows.length === 1 ? '' : 's'} connected
        </div>
      </div>

      <div className="bs-card p-5">
        <h2 className="text-xl font-bold lg:text-2xl">Capabilities</h2>
        <p className="mb-3 mt-1 text-sm text-bluesphere-textSecondary">
          What the connector declares (Story 18). Which of it runs, and on which Client, is decided
          per Client in its Response settings and by the safety rails on every request (Story 19).
        </p>
        {manifest ? (
          <>
            <CapabilitiesLine manifest={manifest} />
            <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
              {Object.entries(manifest.actions).map(([asset, actions]) => (
                <div key={asset} className="min-w-0">
                  <dt className="text-xs uppercase tracking-wider text-bluesphere-muted">
                    {asset} actions
                  </dt>
                  <dd className="text-bluesphere-textOnLight">
                    {actions.length
                      ? actions
                          .map((a) => {
                            const release = manifest.releases[a];
                            const label = a.replaceAll('_', ' ');
                            return release ? `${label} (released by ${release.replaceAll('_', ' ')})` : label;
                          })
                          .join(' · ')
                      : '—'}
                  </dd>
                </div>
              ))}
            </dl>
          </>
        ) : integrations.isError ? (
          <ErrorNote error={integrations.error} />
        ) : (
          <Loading height="h-12" />
        )}
      </div>

      {mapping && (
        <PlatformCredentialCard
          name={vendor}
          status={platform.data}
          statusError={platform.error}
          canManage={can('manage_platform')}
          idToken={idToken}
        />
      )}

      <div className="bs-card">
        <div className="flex flex-wrap items-baseline justify-between gap-2 px-5 pt-5">
          <h2 className="text-xl font-bold lg:text-2xl">Clients</h2>
          <span className="text-xs text-bluesphere-muted">{page.credentialNote}</span>
        </div>
        {(reissueMutation.error != null || reconsentMutation.error != null) && (
          <div className="px-5 pt-3">
            <ErrorNote error={reissueMutation.error ?? reconsentMutation.error} />
          </div>
        )}
        {tenants.isPending ? (
          <Loading height="h-40" />
        ) : tenants.isError ? (
          <div className="p-5">
            <ErrorState
              title="Could not load Clients"
              error={tenants.error}
              onRetry={() => void tenants.refetch()}
            />
          </div>
        ) : rows.length === 0 ? (
          <div className="p-5">
            <EmptyState
              title="No Clients yet"
              body="Add a Client on the Clients page; its connections appear here."
            />
          </div>
        ) : (
          <ClientConnections
            name={name}
            rows={rows}
            onEnable={(t) => enableMutation.mutate({ clientTenantId: t.client_tenant_id })}
            enablePendingFor={
              enableMutation.isPending ? enableMutation.variables?.clientTenantId : undefined
            }
            enableError={enableMutation.error}
            canAddService={Boolean(can('manage_platform'))}
            onEnableAddService={() => {
              const last = enableMutation.variables;
              if (last) enableMutation.mutate({ ...last, addService: true });
            }}
            canManage={(tenantId) =>
              mapping ? Boolean(can('manage_platform')) : can('manage_client', tenantId)
            }
            onMap={setMapFor}
            platformConnected={Boolean(platform.data?.connected)}
            onCredential={setCredentialFor}
            onRemove={setPendingRemove}
            onReissue={(t) => reissueMutation.mutate({ clientTenantId: t.client_tenant_id })}
            reissuePendingFor={
              reissueMutation.isPending ? reissueMutation.variables?.clientTenantId : undefined
            }
            onResponseToken={setResponseTokenFor}
            onRemoveResponseToken={setPendingTokenRemove}
            onReconsent={(t) => reconsentMutation.mutate({ tenant: t })}
            reconsentPendingFor={
              reconsentMutation.isPending
                ? reconsentMutation.variables?.tenant.client_tenant_id
                : undefined
            }
          />
        )}
      </div>

      {credentialFor && name === 'sentinelone' && (
        <SentinelOneModal
          tenant={credentialFor}
          onCancel={() => {
            setCredentialFor(null);
            credentialMutation.reset();
          }}
          onSubmit={(consoleUrl, apiToken) =>
            credentialMutation.mutate({
              clientTenantId: credentialFor.client_tenant_id,
              consoleUrl,
              apiToken,
            })
          }
          isPending={credentialMutation.isPending}
          error={credentialMutation.error}
          canAddService={Boolean(can('manage_platform'))}
          onAddService={() => {
            const last = credentialMutation.variables;
            if (last) credentialMutation.mutate({ ...last, addService: true });
          }}
        />
      )}

      {mapFor && mapping && (
        <VendorMappingModal
          name={vendor}
          tenant={mapFor}
          idToken={idToken}
          onCancel={() => {
            setMapFor(null);
            mapMutation.reset();
          }}
          onSubmit={(externalId) =>
            mapMutation.mutate({ clientTenantId: mapFor.client_tenant_id, externalId })
          }
          isPending={mapMutation.isPending}
          error={mapMutation.error}
          canAddService={Boolean(can('manage_platform'))}
          onAddService={() => {
            const last = mapMutation.variables;
            if (last) mapMutation.mutate({ ...last, addService: true });
          }}
        />
      )}

      {pendingRemove && mapping && (
        <ConfirmModal
          title={`Unmap ${pendingRemove.display_name} from ${sourceLabel(name)}?`}
          confirmLabel="Unmap"
          pendingLabel="Unmapping…"
          onCancel={() => {
            setPendingRemove(null);
            removeMutation.reset();
          }}
          onConfirm={() =>
            removeMutation.mutate({ clientTenantId: pendingRemove.client_tenant_id })
          }
          isPending={removeMutation.isPending}
          error={removeMutation.error}
        >
          <p className="text-sm text-bluesphere-textSecondary">
            Nothing more is read from or written to {sourceLabel(name)} for{' '}
            <strong className="text-bluesphere-textOnLight">{pendingRemove.display_name}</strong>, and
            the {vendorNoun(vendor)}{' '}
            <strong className="text-bluesphere-textOnLight">
              {mappedName(pendingRemove, vendor) ?? ''}
            </strong>{' '}
            is free to map to another Client.{' '}
            {name === 'ninjaone' ? 'Tickets already in NinjaOne stay there.' : ''}
          </p>
        </ConfirmModal>
      )}

      {pendingRemove && !mapping && (
        <ConfirmModal
          title={page.connectsBy === 'enable' ? `Stop ${sourceLabel(name)}?` : `Remove ${sourceLabel(name)}?`}
          confirmLabel={page.connectsBy === 'enable' ? 'Stop' : 'Remove source'}
          pendingLabel="Removing…"
          onCancel={() => {
            setPendingRemove(null);
            removeMutation.reset();
          }}
          onConfirm={() =>
            removeMutation.mutate({ clientTenantId: pendingRemove.client_tenant_id })
          }
          isPending={removeMutation.isPending}
          error={removeMutation.error}
        >
          <p className="text-sm text-bluesphere-textSecondary">
            Polling stops for {sourceLabel(name)} on{' '}
            <strong className="text-bluesphere-textOnLight">{pendingRemove.display_name}</strong>
            {page.connectsBy === 'token' && ' and the stored token is deleted immediately'}. Other
            sources are unaffected.{' '}
            {page.connectsBy === 'enable'
              ? 'Events already stored stay searchable until they expire at 90 days.'
              : 'Existing detections stay visible.'}
          </p>
        </ConfirmModal>
      )}

      {activeInvite && (
        <InviteModal
          tenant={activeInvite}
          onClose={() => {
            setActiveInvite(null);
            reissueMutation.reset();
          }}
        />
      )}

      {responseTokenFor && page.responseCredential && (
        <ResponseTokenModal
          tenant={responseTokenFor}
          onCancel={() => {
            setResponseTokenFor(null);
            responseTokenMutation.reset();
          }}
          onSubmit={(apiToken) =>
            responseTokenMutation.mutate({
              clientTenantId: responseTokenFor.client_tenant_id,
              apiToken,
            })
          }
          isPending={responseTokenMutation.isPending}
          error={responseTokenMutation.error}
        />
      )}

      {pendingTokenRemove && page.responseCredential && (
        <ConfirmModal
          title={`Remove the ${page.responseCredential.label}?`}
          confirmLabel="Remove token"
          pendingLabel="Removing…"
          onCancel={() => {
            setPendingTokenRemove(null);
            removeTokenMutation.reset();
          }}
          onConfirm={() =>
            removeTokenMutation.mutate({ clientTenantId: pendingTokenRemove.client_tenant_id })
          }
          isPending={removeTokenMutation.isPending}
          error={removeTokenMutation.error}
        >
          <p className="text-sm text-bluesphere-textSecondary">
            The stored {page.responseCredential.label} for{' '}
            <strong className="text-bluesphere-textOnLight">{pendingTokenRemove.display_name}</strong>{' '}
            is deleted immediately. Ingestion continues on the read token; response actions on this
            Client's {sourceLabel(name)} endpoints become unavailable, with that reason shown.
          </p>
        </ConfirmModal>
      )}

      {reconsent && (
        <ReconsentModal
          displayName={reconsent.tenant.display_name}
          result={reconsent.result}
          onClose={() => {
            setReconsent(null);
            reconsentMutation.reset();
          }}
        />
      )}
    </div>
  );
}

// ----- The per-Client connections table ---------------------------------------

function ClientConnections({
  name,
  rows,
  onEnable,
  enablePendingFor,
  enableError,
  canAddService,
  onEnableAddService,
  canManage,
  onCredential,
  onRemove,
  onReissue,
  reissuePendingFor,
  onResponseToken,
  onRemoveResponseToken,
  onReconsent,
  reconsentPendingFor,
  onMap,
  platformConnected,
}: {
  name: PagedIntegrationName;
  rows: ClientTenant[];
  onEnable: (tenant: ClientTenant) => void;
  enablePendingFor?: string;
  enableError: unknown;
  canAddService: boolean;
  onEnableAddService: () => void;
  /** `manage_client` on this Client: every move below needs it. */
  canManage: (tenantId: string) => boolean;
  onCredential: (tenant: ClientTenant) => void;
  onRemove: (tenant: ClientTenant) => void;
  onReissue: (tenant: ClientTenant) => void;
  reissuePendingFor?: string;
  onResponseToken: (tenant: ClientTenant) => void;
  onRemoveResponseToken: (tenant: ClientTenant) => void;
  onReconsent: (tenant: ClientTenant) => void;
  reconsentPendingFor?: string;
  onMap: (tenant: ClientTenant) => void;
  platformConnected: boolean;
}) {
  // The column exists when any listed Client is manageable; a row the
  // caller cannot manage leaves its cell empty.
  const hasActions = rows.some((t) => canManage(t.client_tenant_id));
  const actionsFor = (tenant: ClientTenant) =>
    canManage(tenant.client_tenant_id) ? (
      <ConnectionActions
        name={name}
        tenant={tenant}
        onEnable={onEnable}
        enablePending={enablePendingFor === tenant.client_tenant_id}
        onCredential={onCredential}
        onRemove={onRemove}
        onReissue={onReissue}
        reissuePending={reissuePendingFor === tenant.client_tenant_id}
        onResponseToken={onResponseToken}
        onRemoveResponseToken={onRemoveResponseToken}
        onReconsent={onReconsent}
        reconsentPending={reconsentPendingFor === tenant.client_tenant_id}
        onMap={onMap}
        platformConnected={platformConnected}
      />
    ) : null;

  return (
    <div className="mt-3">
      {enableError != null && (
        <div className="px-5 pb-3">
          <ConnectErrorNote
            error={enableError}
            canAddService={canAddService}
            onAddService={onEnableAddService}
            isPending={enablePendingFor != null}
          />
        </div>
      )}
      {/* Phone: one card per Client (task 75 idiom). */}
      <ul className="divide-y divide-bluesphere-border md:hidden">
        {rows.map((tenant) => {
          const info = tenant.sources[name];
          return (
            <li key={tenant.client_tenant_id} className="space-y-2 px-5 py-4 text-sm">
              <div className="flex items-start justify-between gap-3">
                <Link to="/organizations" className="min-w-0 font-medium text-bluesphere-textOnLight">
                  {tenant.display_name}
                </Link>
                <ConnectionBadge state={connectionState(tenant, name)} />
              </div>
              <HealthLine info={info} />
              <Details info={info} />
              <ResponseLine name={name} tenant={tenant} />
              {canManage(tenant.client_tenant_id) && (
                <div className="pt-1">{actionsFor(tenant)}</div>
              )}
            </li>
          );
        })}
      </ul>

      {/* Desktop: the table. */}
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full text-sm">
          <thead className="border-b border-bluesphere-border bg-bluesphere-surfaceMuted">
            <tr className="text-left text-xs uppercase tracking-wider text-bluesphere-textSecondary">
              <th className="px-5 py-3">Client</th>
              <th className="px-4 py-3">Connection</th>
              <th className="px-4 py-3">Health</th>
              <th className="px-4 py-3">Watermark</th>
              <th className="px-4 py-3">Response</th>
              {hasActions && <th className="px-5 py-3 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((tenant) => {
              const info = tenant.sources[name];
              return (
                <tr
                  key={tenant.client_tenant_id}
                  className="border-t border-bluesphere-border align-top"
                >
                  <td className="px-5 py-3">
                    <Link
                      to="/organizations"
                      className="font-medium text-bluesphere-textOnLight hover:underline"
                    >
                      {tenant.display_name}
                    </Link>
                    <div className="mt-0.5 font-mono text-xs text-bluesphere-muted">
                      {tenant.client_tenant_id}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <ConnectionBadge state={connectionState(tenant, name)} />
                    {info?.connected_at && (
                      <div className="mt-1 text-xs text-bluesphere-muted">
                        since {timeAgo(info.connected_at)}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <HealthLine info={info} />
                  </td>
                  <td className="px-4 py-3">
                    <Details info={info} />
                  </td>
                  <td className="px-4 py-3">
                    <ResponseLine name={name} tenant={tenant} />
                  </td>
                  {hasActions && (
                    <td className="px-5 py-3 text-right">
                      <div className="flex flex-wrap items-center justify-end gap-3">
                        {actionsFor(tenant)}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function HealthLine({ info }: { info: SourceInfo | undefined }) {
  if (!info) return <span className="text-xs text-bluesphere-muted">—</span>;
  const removed = info.state === 'removed';
  return (
    <div className="space-y-0.5">
      <div className="flex flex-wrap items-center gap-2">
        <HealthChip status={removed ? 'pending' : (info.health ?? 'pending')} />
        <span className="text-xs text-bluesphere-muted" title={info.last_success_at ?? undefined}>
          {syncSummary(info)}
        </span>
      </div>
      {!removed && info.last_error && info.health !== 'healthy' && (
        <div className="max-w-xs truncate text-xs text-red-700" title={info.last_error}>
          {info.last_error}
          {info.last_error_at && ` · ${timeAgo(info.last_error_at)}`}
        </div>
      )}
    </div>
  );
}

function Details({ info }: { info: SourceInfo | undefined }) {
  if (!info || info.state === 'removed') {
    return <span className="text-xs text-bluesphere-muted">—</span>;
  }
  if (info.feeds) return <FeedDetails feeds={info.feeds} />;
  if (info.config) {
    const orgName = info.config.organization_name ?? info.config.company_name;
    const orgId = info.config.organization_id ?? info.config.company_id;
    return (
      <div className="text-xs text-bluesphere-textSecondary">
        <div className="text-bluesphere-textOnLight">{orgName || orgId}</div>
        <div className="font-mono text-bluesphere-muted">id {orgId}</div>
      </div>
    );
  }
  return (
    <div className="text-xs text-bluesphere-textSecondary">
      <div title={info.watermark ?? undefined}>
        {info.watermark ? absoluteTime(info.watermark) : 'no watermark yet'}
      </div>
      <div className="text-bluesphere-muted">
        {info.backfilling ? 'back-filling' : 'live'}
        {typeof info.consecutive_failures === 'number' && info.consecutive_failures > 0
          ? ` · ${info.consecutive_failures} consecutive failure${info.consecutive_failures === 1 ? '' : 's'}`
          : ''}
      </div>
    </div>
  );
}

/**
 * An event source's feeds (task 157): each one's state and how far it has
 * read. `unlicensed` is coverage, not a fault — the provider refuses that
 * feed without a license, and the source is healthy on the others.
 */
function FeedDetails({ feeds }: { feeds: NonNullable<SourceInfo['feeds']> }) {
  const entries = Object.entries(feeds) as [EventFeed, NonNullable<SourceInfo['feeds']>[EventFeed]][];
  return (
    <ul className="space-y-0.5 text-xs text-bluesphere-textSecondary">
      {entries.map(([feed, health]) => {
        if (!health) return null;
        let text: string;
        if (health.state === 'unlicensed') {
          text = isSignInFeed(feed) ? 'require Entra ID P1' : 'not licensed';
        } else if (health.state === 'error') {
          text = 'failing';
        } else if (health.state === null) {
          text = 'first poll pending';
        } else {
          text = health.watermark ? `to ${absoluteTime(health.watermark)}` : 'reading';
        }
        const tone =
          health.state === 'error'
            ? 'text-red-700'
            : health.state === 'unlicensed'
              ? 'text-amber-800'
              : '';
        return (
          <li key={feed} className={tone} title={health.last_error ?? undefined}>
            {feedLabel(feed)} {text}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Where this Client stands for response actions on this integration
 * (Story 19): the write-scoped credential for a token integration, the
 * consent coverage for a consent integration. The rails read the same
 * facts; this is the operator's view of what they will say.
 */
function ResponseLine({ name, tenant }: { name: PagedIntegrationName; tenant: ClientTenant }) {
  const page = INTEGRATION_PAGES[name];
  const state = connectionState(tenant, name);
  if (state !== 'connected' || page.connectsBy === 'mapping') {
    return <span className="text-xs text-bluesphere-muted">—</span>;
  }
  if (page.responseCredential) {
    const present = tenant.sources[name]?.response_token === 'present';
    return (
      <span className={`text-xs ${present ? 'text-bluesphere-textOnLight' : 'text-bluesphere-muted'}`}>
        {present ? `${page.responseCredential.label} present` : `no ${page.responseCredential.label}`}
      </span>
    );
  }
  const missing = tenant.missing_scopes.length;
  return (
    <span
      className={`text-xs ${missing ? 'text-amber-800' : 'text-bluesphere-textOnLight'}`}
      title={missing ? tenant.missing_scopes.join(', ') : 'Consent covers every declared permission.'}
    >
      {missing ? `${missing} permission${missing === 1 ? '' : 's'} not yet granted` : 'permissions current'}
    </span>
  );
}

/**
 * The `manage_client` moves for one Client on this integration. One
 * component for the table cell and the phone card, so the two can never
 * offer different moves. Which moves exist follows `connectsBy` and,
 * since Story 19, `responseCredential` / the Client's `missing_scopes`.
 */
function ConnectionActions({
  name,
  tenant,
  onEnable,
  enablePending,
  onCredential,
  onRemove,
  onReissue,
  reissuePending,
  onResponseToken,
  onRemoveResponseToken,
  onReconsent,
  reconsentPending,
  onMap,
  platformConnected,
}: {
  name: PagedIntegrationName;
  tenant: ClientTenant;
  onEnable: (tenant: ClientTenant) => void;
  enablePending: boolean;
  onCredential: (tenant: ClientTenant) => void;
  onRemove: (tenant: ClientTenant) => void;
  onReissue: (tenant: ClientTenant) => void;
  reissuePending: boolean;
  onResponseToken: (tenant: ClientTenant) => void;
  onRemoveResponseToken: (tenant: ClientTenant) => void;
  onReconsent: (tenant: ClientTenant) => void;
  reconsentPending: boolean;
  onMap: (tenant: ClientTenant) => void;
  platformConnected: boolean;
}) {
  const page = INTEGRATION_PAGES[name];
  const state = connectionState(tenant, name);
  const plain = 'text-xs text-bluesphere-textOnLight underline hover:text-bluesphere-secondary';
  const danger = 'text-xs text-red-700 underline hover:text-red-900';

  // Mapping integrations (task 161): the platform credential first, then
  // Map / Change / Unmap against the vendor's organization list.
  if (page.connectsBy === 'mapping') {
    const vendor = name as PlatformIntegrationName;
    if (!platformConnected) {
      return <span className="text-xs text-bluesphere-muted">connect {sourceLabel(name)} first</span>;
    }
    if (state === 'connected') {
      return (
        <div className="flex flex-wrap items-center gap-3 md:justify-end">
          <button type="button" onClick={() => onMap(tenant)} className={plain}>
            Change {vendorNoun(vendor)}
          </button>
          <button type="button" onClick={() => onRemove(tenant)} className={danger}>
            Unmap
          </button>
        </div>
      );
    }
    return (
      <div className="flex flex-wrap items-center gap-3 md:justify-end">
        <button type="button" onClick={() => onMap(tenant)} className={plain}>
          {state === 'removed' ? 'Map again' : `Map ${vendorNoun(vendor)}`}
        </button>
      </div>
    );
  }

  if (page.connectsBy === 'token') {
    if (tenant.status !== 'consented') {
      return (
        <span className="text-xs text-bluesphere-muted" title="Complete Defender consent first.">
          after consent
        </span>
      );
    }
    const tokenPresent = tenant.sources[name]?.response_token === 'present';
    return (
      <div className="flex flex-wrap items-center gap-3 md:justify-end">
        {state === 'connected' ? (
          <>
            <button
              type="button"
              onClick={() => onCredential(tenant)}
              className={plain}
              title="Replace the stored token"
            >
              Rotate token
            </button>
            <button type="button" onClick={() => onRemove(tenant)} className={danger}>
              Remove
            </button>
            {page.responseCredential && (
              <>
                <span className="text-bluesphere-borderMuted" aria-hidden="true">
                  |
                </span>
                <button
                  type="button"
                  onClick={() => onResponseToken(tenant)}
                  className={plain}
                  title={page.responseCredential.note}
                >
                  {tokenPresent
                    ? `Rotate ${page.responseCredential.label}`
                    : `Add ${page.responseCredential.label}`}
                </button>
                {tokenPresent && (
                  <button
                    type="button"
                    onClick={() => onRemoveResponseToken(tenant)}
                    className={danger}
                  >
                    Remove {page.responseCredential.label}
                  </button>
                )}
              </>
            )}
          </>
        ) : (
          <button type="button" onClick={() => onCredential(tenant)} className={plain}>
            {state === 'removed' ? 'Add again' : `Add ${sourceLabel(name)}`}
          </button>
        )}
      </div>
    );
  }

  // Enable integrations (task 157): consent first, then the permission,
  // then an explicit Enable. Stop is the ordinary source removal.
  if (page.connectsBy === 'enable') {
    if (state === 'pending') {
      return (
        <span className="text-xs text-bluesphere-muted">
          after {sourceLabel(page.ridesConsentOf ?? 'defender')} consent
        </span>
      );
    }
    if (state === 'connected') {
      return (
        <div className="flex flex-wrap items-center gap-3 md:justify-end">
          <button type="button" onClick={() => onRemove(tenant)} className={danger}>
            Stop
          </button>
        </div>
      );
    }
    if (tenant.missing_scopes.includes(ENTRA_LOGS_SCOPE)) {
      return (
        <div className="flex flex-wrap items-center gap-3 md:justify-end">
          <span className="text-xs text-bluesphere-muted">needs {ENTRA_LOGS_SCOPE}</span>
          <button
            type="button"
            onClick={() => onReconsent(tenant)}
            disabled={reconsentPending}
            className={`${plain} disabled:opacity-60`}
            title={`Mint a consent link that adds: ${tenant.missing_scopes.join(', ')}`}
          >
            {reconsentPending ? 'Minting…' : 'Update permissions'}
          </button>
        </div>
      );
    }
    return (
      <div className="flex flex-wrap items-center gap-3 md:justify-end">
        <button
          type="button"
          onClick={() => onEnable(tenant)}
          disabled={enablePending}
          className={`${plain} disabled:opacity-60`}
          title="Checks the permission and reads the last seven days."
        >
          {enablePending ? 'Enabling…' : state === 'removed' ? 'Enable again' : 'Enable'}
        </button>
      </div>
    );
  }

  // Consent integrations: the connection is the Client's admin consent.
  // An integration that rides another's consent has no link of its own to
  // mint — the move belongs to the integration that owns the consent.
  if (page.ridesConsentOf && state === 'pending') {
    return (
      <span className="text-xs text-bluesphere-muted">
        with {sourceLabel(page.ridesConsentOf)} consent
      </span>
    );
  }
  if (state === 'pending') {
    return (
      <div className="flex flex-wrap items-center gap-3 md:justify-end">
        <button
          type="button"
          onClick={() => onReissue(tenant)}
          disabled={reissuePending}
          className={`${plain} disabled:opacity-60`}
          title="Mint a fresh consent link. The previous link stops working."
        >
          {reissuePending ? 'Minting…' : 'Re-mint invite'}
        </button>
      </div>
    );
  }
  if (state === 'connected' && tenant.missing_scopes.length > 0) {
    return (
      <div className="flex flex-wrap items-center gap-3 md:justify-end">
        <button
          type="button"
          onClick={() => onReconsent(tenant)}
          disabled={reconsentPending}
          className={`${plain} disabled:opacity-60`}
          title={`Mint a consent link that adds: ${tenant.missing_scopes.join(', ')}`}
        >
          {reconsentPending ? 'Minting…' : 'Update permissions'}
        </button>
      </div>
    );
  }
  return (
    <span className="text-xs text-bluesphere-muted">
      {state === 'connected' ? 'consented' : '—'}
    </span>
  );
}
